import { useState } from "react";
import { useHistory } from "react-router-dom";
import Sidebar from "@/pages/sidebar/sidebar";
import ClientMainHeader from "@/pages/header";
import { MainWrapper } from "@/pages/styled";
import { LimitedAccountWarning } from "@/components/limited-account-warning";
import { MerchantProcessingBanner } from "@/components/merchant-processing-banner";
import { cn } from "@/utils/classnames";
import InvoiceTable from "@/components/invoices/invoice-table";
import BulkImportTable from "../../components/invoices/bulk-import-table";
import { RecurringInvoicesTable } from "@/components/invoices/recurring-invoices";
import PayablesTable from "@/components/invoices/payables-table";
import { Button } from "@/components/ui/button-tw";
import { Settings } from "lucide-react";
import { useTenancy } from "@/hooks/tenancy/use-tenancy";
import { useAuth } from "@/providers/auth";
import { useCustomerPayments } from "@/queries/customer-payments/use-customer-payments";
import { CustomerPaymentsTable } from "@/components/invoices/customer-payments-table";

const Invoices = () => {
  const history = useHistory();
  const [shortCut, setShortCut] = useState("");
  const { data: tenancy } = useTenancy();
  const { cognitoId, clientId, userRole } = useAuth();
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;
  const isNewNav = tenancy?.feature_flags?.new_nav;

  const allTabs = [
    { name: "Invoices & Quick Pays" },
    ...(!isNewNav ? [{ name: "Payables" }] : []),
    { name: "Recurring Invoices", featureFlag: "recurring_invoices" },
    ...(isNewNav ? [{ name: "Customer Payments" }] : []),
    { name: "Bulk Upload" },
  ];

  const tabs = allTabs.filter(
    (tab) => !tab.featureFlag || tenancy?.feature_flags?.[tab.featureFlag],
  );

  const [currTab, setCurrTab] = useState(tabs[0]);
  const [currentWidth, setCurrentWidth] = useState(0);

  const { data: customerPayments, isLoading: customerPaymentsLoading } =
    useCustomerPayments(userId || "", {
      enabled: !!userId && currTab.name === "Customer Payments",
    });

  return (
    <div className="h-100">
      <Sidebar currentWidth={currentWidth} setCurrentWidth={setCurrentWidth} />
      <>
        <LimitedAccountWarning />
        <MerchantProcessingBanner />

        <MainWrapper>
          <div className="flex justify-between">
            <ClientMainHeader
              title={isNewNav ? "Get Paid" : "Invoices"}
              shortCut={shortCut}
              setShortCut={setShortCut}
            />
            <Button
              variant="outline"
              onClick={() => history.push("/invoices/settings")}
              className="flex items-center gap-2 mt-2"
            >
              <Settings size={16} />
              Invoice Settings
            </Button>
          </div>

          <InvoicesTab currTab={currTab} setTab={setCurrTab} tabs={tabs} />
          {currTab.name === "Recurring Invoices" ? (
            <RecurringInvoicesTable />
          ) : currTab.name === "Bulk Upload" ? (
            <BulkImportTable />
          ) : currTab.name === "Payables" ? (
            <PayablesTable />
          ) : currTab.name === "Customer Payments" ? (
            <CustomerPaymentsTable
              data={customerPayments ?? []}
              isLoading={customerPaymentsLoading}
            />
          ) : (
            <InvoiceTable />
          )}
        </MainWrapper>
      </>
    </div>
  );
};

export function InvoicesTab({ currTab, setTab, tabs }) {
  return (
    <div className="mb-6">
      <div className="">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            {tabs.map((tab) => (
              <button
                key={tab.name}
                onClick={() => setTab(tab)}
                className={cn(
                  tab.name === currTab?.name
                    ? "border-skin-primary/90 text-skin-primary"
                    : "border-transparent text-gray-500 hover:border-gray-200 hover:text-gray-700",
                  "flex whitespace-nowrap border-b-2 px-1 py-4 text-sm font-medium",
                )}
                aria-current={tab.name === currTab?.name ? "page" : undefined}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </div>
      </div>
    </div>
  );
}

export default Invoices;
